import { Ingredient, Tag, TagImplication } from '../api/types.js';
import {
  listTagsWithUsage, createOrGetTag, updateTag, deleteTag, getIngredientsForTag,
  listTagImplications, setTagImplications,
} from '../api/index.js';
import { authService } from '../auth/AuthService.js';
import { editModeService } from '../edit-mode/EditModeService.js';
import { getDefaultImageUrl } from '../recipe_renderer/imageUtils.js';
import { showToast } from '../ui/toast.js';
import { TAG_COLORS, getTagColorHex, tagChipStyle } from './tagColors.js';
import { tagGraphCache } from './tagGraph.js';
import { TagPicker } from '../recipe_renderer/components/TagPicker.js';

// Same glyphs ExplorerPage.ts uses for its own toolbar/row actions.
const ICON_CREATE = '+';
const ICON_EDIT = '✎';
const ICON_DELETE = '×';
const ICON_SAVE = '✓';
const ICON_CANCEL = '✕';

/** Ingredients shown at once in the "Ingredients" section, before paging
 *  kicks in — same page size as ExplorerPage's own recipe lists. */
const INGREDIENTS_PER_PAGE = 5;

interface TagRow {
  tag: Tag;
  ingredientCount: number;
}

/**
 * Tags explorer: a table of every registered tag (with its ingredient-usage
 * count), mirroring ExplorerPage.ts's accordion pattern but for tags' much
 * smaller shape — no images, no description, no merge; the one thing tags
 * have that ingredients/transformations don't is a color, so create/edit
 * gets a 16-swatch color picker instead.
 *
 * Browsing is public; create/edit/delete only appear when both
 * authenticated *and* the global edit-mode toggle is on — same gate as
 * ExplorerPage.
 */
export class TagsExplorerPage {
  private container: HTMLElement;
  private rows: TagRow[] = [];
  /** Every "implies" edge, flat — loaded once alongside the rows, kept in
   *  sync locally as the picker/delete change it, so the read-only
   *  "Implica:" line and the edit form's checkboxes never need a full
   *  page reload to reflect a just-made change. */
  private implications: TagImplication[] = [];
  private filterText: string = '';
  private selectedId: string | null = null;
  private isCreating: boolean = false;
  private isEditingCard: boolean = false;
  private unsubscribeAuth: (() => void) | null = null;
  private unsubscribeEditMode: (() => void) | null = null;

  private tableContainer: HTMLElement | null = null;
  private rowsListEl: HTMLElement | null = null;
  private toolbarContainer: HTMLElement | null = null;
  /** The create/edit form's "Implica" TagPicker, if one is currently
   *  mounted — kept so its outside-click listener can be torn down (see
   *  TagPicker.destroy()'s own doc comment) whenever the row it lives in
   *  is about to be replaced by a re-render. */
  private impliesPicker: TagPicker | null = null;

  constructor(container: HTMLElement) {
    this.container = container;
  }

  async render(): Promise<void> {
    this.container.innerHTML = '';

    const page = document.createElement('div');
    page.className = 'py-4 max-w-full px-4';

    // No page heading — SiteNav already names this section ("Etiquetes").
    this.toolbarContainer = document.createElement('div');
    this.toolbarContainer.className = 'mb-3';
    page.appendChild(this.toolbarContainer);

    this.tableContainer = document.createElement('div');
    this.tableContainer.className = 'border border-[var(--accent-alpha-20)] rounded-lg overflow-hidden';
    page.appendChild(this.tableContainer);

    this.container.appendChild(page);

    try {
      await this.loadRows();
    } catch (error) {
      console.error('Failed to load tags:', error);
      showToast('No s\'han pogut carregar les etiquetes', 'error');
    }

    this.renderToolbar();
    this.renderTable();

    const onEditabilityChange = (): void => {
      if (!this.canEdit()) {
        this.isEditingCard = false;
        this.isCreating = false;
      }
      this.renderToolbar();
      this.refreshRows();
    };
    this.unsubscribeAuth = authService.subscribe(onEditabilityChange);
    this.unsubscribeEditMode = editModeService.subscribe(onEditabilityChange);
  }

  destroy(): void {
    this.unsubscribeAuth?.();
    this.unsubscribeAuth = null;
    this.unsubscribeEditMode?.();
    this.unsubscribeEditMode = null;
    this.impliesPicker?.destroy();
    this.impliesPicker = null;
  }

  private canEdit(): boolean {
    return authService.isAuthenticated() && editModeService.isEditMode();
  }

  private async loadRows(): Promise<void> {
    const [items, implications] = await Promise.all([listTagsWithUsage(), listTagImplications()]);
    this.rows = items.map(i => ({ tag: i.tag, ingredientCount: i.ingredientCount }));
    this.rows.sort((a, b) => a.tag.name.localeCompare(b.tag.name));
    this.implications = implications;
  }

  /** This tag's direct outgoing edges — what it implies, not transitively,
   *  just the ones actually stored for it. */
  private impliesIdsOf(tagId: string): string[] {
    return this.implications.filter(i => i.fromTagId === tagId).map(i => i.toTagId);
  }

  // ============================================================================
  // Toolbar: create
  // ============================================================================

  private renderToolbar(): void {
    if (!this.toolbarContainer) return;
    this.toolbarContainer.innerHTML = '';
    if (!this.canEdit()) return;

    const row = document.createElement('div');
    row.className = 'flex flex-wrap items-center gap-2';

    const createBtn = this.buildIconButton(ICON_CREATE, 'Nova etiqueta', false);
    createBtn.addEventListener('click', () => {
      this.isCreating = true;
      this.isEditingCard = false;
      this.selectedId = null;
      this.renderToolbar();
      this.refreshRows();
    });
    row.appendChild(createBtn);

    // Edit/delete for whichever single row is selected — also reachable
    // from the row itself (buildRowElement()).
    if (!this.isEditingCard && this.selectedId) {
      const selectedRow = this.rows.find(r => r.tag.id === this.selectedId);
      if (selectedRow) {
        row.appendChild(this.buildEditIconButton());
        row.appendChild(this.buildDeleteIconButton(selectedRow));
      }
    }

    this.toolbarContainer.appendChild(row);
  }

  // ============================================================================
  // Table
  // ============================================================================

  private renderTable(): void {
    if (!this.tableContainer) return;
    this.tableContainer.innerHTML = '';

    const filterWrap = document.createElement('div');
    filterWrap.className = 'p-2 border-b border-[var(--accent-alpha-20)]';
    const filterInput = document.createElement('input');
    filterInput.type = 'text';
    filterInput.placeholder = 'Filtra etiquetes...';
    filterInput.value = this.filterText;
    filterInput.className = 'w-full px-3 py-2 bg-[#0d1117] border border-[#333] rounded-md text-[#e0e0e0] placeholder-[#666] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]';
    filterWrap.appendChild(filterInput);
    this.tableContainer.appendChild(filterWrap);

    filterInput.addEventListener('input', () => {
      this.filterText = filterInput.value;
      this.refreshRows();
    });

    this.rowsListEl = document.createElement('div');
    this.rowsListEl.className = 'max-h-[70vh] overflow-y-auto divide-y divide-[var(--accent-alpha-13)]';
    this.tableContainer.appendChild(this.rowsListEl);

    this.refreshRows();
  }

  private refreshRows(): void {
    if (!this.rowsListEl) return;
    const scrollTop = this.rowsListEl.scrollTop;
    this.renderRows(this.rowsListEl);
    this.rowsListEl.scrollTop = scrollTop;
  }

  private matchesFilter(name: string): boolean {
    const q = this.filterText.trim().toLowerCase();
    if (!q) return true;
    return name.toLowerCase().includes(q);
  }

  private renderRows(rowsListEl: HTMLElement): void {
    // Whatever form (with its own "Implica" TagPicker) was mounted in the
    // previous render is about to be discarded wholesale below.
    this.impliesPicker?.destroy();
    this.impliesPicker = null;

    rowsListEl.innerHTML = '';

    if (this.isCreating) {
      rowsListEl.appendChild(this.buildCreateRowElement());
    }

    const filtered = this.rows.filter(r => this.matchesFilter(r.tag.name));

    if (filtered.length === 0) {
      if (!this.isCreating) {
        const empty = document.createElement('div');
        empty.className = 'p-4 text-sm text-[#888] text-center';
        empty.textContent = 'Cap resultat';
        rowsListEl.appendChild(empty);
      }
      return;
    }

    for (const row of filtered) {
      rowsListEl.appendChild(this.buildRowElement(row));
    }
  }

  /** One row: a swatch dot + name summary line, plus — if this is the
   *  single expanded row — the read-only detail or edit form underneath. */
  private buildRowElement(row: TagRow): HTMLElement {
    const { tag } = row;
    const isExpanded = this.selectedId === tag.id;

    const wrapper = document.createElement('div');

    const summary = document.createElement('div');
    summary.className = 'px-3 py-2 flex items-center gap-3 cursor-pointer transition-colors border-l-2 ' +
      (isExpanded ? 'bg-[var(--accent-alpha-13)] border-[var(--accent)]' : 'hover:bg-[var(--accent-alpha-08)] border-transparent');

    // Swatch + name are omitted once expanded — the content below
    // (mountViewContent/mountEditForm) already opens with its own
    // swatch + name "title", so showing both read as a duplicate.
    if (!isExpanded) {
      summary.appendChild(this.buildSwatchDot(tag.color));

      const nameSpan = document.createElement('span');
      nameSpan.textContent = tag.name;
      nameSpan.className = 'flex-1 text-sm text-[#e0e0e0] truncate';
      summary.appendChild(nameSpan);
    } else {
      // Keeps the row's height/spacing consistent and pushes the
      // edit/delete actions below to the right, same as the name span
      // otherwise would.
      const spacer = document.createElement('div');
      spacer.className = 'flex-1';
      summary.appendChild(spacer);
    }

    // Edit/delete for the expanded row, at the right — also reachable
    // from the top toolbar while this row is selected. While actively
    // editing, this same spot instead holds save/cancel for the form
    // below — see editActionsContainer.
    let editActionsContainer: HTMLElement | null = null;
    if (isExpanded && this.canEdit()) {
      const actions = document.createElement('div');
      actions.className = 'flex items-center gap-1 flex-shrink-0';
      if (this.isEditingCard) {
        editActionsContainer = document.createElement('div');
        editActionsContainer.className = 'flex items-center gap-1';
        actions.appendChild(editActionsContainer);
      } else {
        actions.appendChild(this.buildEditIconButton());
        actions.appendChild(this.buildDeleteIconButton(row));
      }
      summary.appendChild(actions);
    }

    summary.addEventListener('click', () => {
      this.isCreating = false;
      if (this.selectedId === tag.id) {
        this.selectedId = null;
        this.isEditingCard = false;
      } else {
        this.selectedId = tag.id;
        this.isEditingCard = false;
      }
      this.renderToolbar();
      this.refreshRows();
    });

    wrapper.appendChild(summary);

    if (isExpanded) {
      const content = document.createElement('div');
      content.className = 'px-4 pt-1 pb-4 border-l-2 border-[var(--accent)] bg-[var(--accent-alpha-08)]';
      if (this.isEditingCard && editActionsContainer) {
        this.mountEditForm(content, row, editActionsContainer);
      } else {
        this.mountViewContent(content, row);
      }
      wrapper.appendChild(content);
    }

    return wrapper;
  }

  private buildSwatchDot(colorKey: string | undefined): HTMLElement {
    const dot = document.createElement('span');
    dot.className = 'rounded-full flex-shrink-0';
    dot.style.cssText = `width: 14px; height: 14px; background: ${getTagColorHex(colorKey)};`;
    return dot;
  }

  /** The virtual expanded row prepended above the real ones while creating. */
  private buildCreateRowElement(): HTMLElement {
    const wrapper = document.createElement('div');
    const content = document.createElement('div');
    content.className = 'px-4 py-3 border-l-2 border-[var(--accent)] bg-[var(--accent-alpha-08)]';
    wrapper.appendChild(content);

    this.mountTagForm(content, null, null, {
      onSaved: (tag) => {
        // save() already reloaded this.rows/this.implications from the
        // server — nothing to patch in here.
        this.isCreating = false;
        this.selectedId = tag.id;
        this.renderToolbar();
        this.refreshRows();
        showToast('Etiqueta creada', 'success');
      },
      onCancelled: () => {
        this.isCreating = false;
        this.refreshRows();
      },
    });

    return wrapper;
  }

  private mountEditForm(container: HTMLElement, row: TagRow, actionsContainer: HTMLElement): void {
    this.mountTagForm(container, row.tag, actionsContainer, {
      onSaved: () => {
        // save() already reloaded this.rows/this.implications from the
        // server — nothing to patch in here.
        this.isEditingCard = false;
        this.renderToolbar();
        this.refreshRows();
        showToast('Desat', 'success');
      },
      onCancelled: () => {
        this.isEditingCard = false;
        this.renderToolbar();
        this.refreshRows();
      },
    });
  }

  /**
   * Shared create/edit form: a name input plus a 16-swatch color picker.
   * `existingTag` null means "create"; otherwise "edit this tag". When
   * `actionsContainer` is given, save/cancel render there (the row's own
   * summary line, ✓/✕) instead of below the form — same convention
   * InlineEntityEditor's onActionsReady uses for ingredients/transformations.
   */
  private mountTagForm(
    container: HTMLElement,
    existingTag: Tag | null,
    actionsContainer: HTMLElement | null,
    callbacks: { onSaved: (tag: Tag) => void; onCancelled: () => void },
  ): void {
    const form = document.createElement('div');
    form.className = 'space-y-3 pt-2';

    const nameInput = document.createElement('input');
    nameInput.type = 'text';
    nameInput.placeholder = 'Nom de l\'etiqueta';
    nameInput.value = existingTag?.name ?? '';
    nameInput.className = 'w-full px-3 py-2 bg-[#0d1117] border border-[#333] rounded-md text-[#e0e0e0] placeholder-[#666] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]';
    form.appendChild(nameInput);

    let selectedColor = existingTag?.color ?? 'green';

    const swatchGrid = document.createElement('div');
    swatchGrid.className = 'flex flex-wrap gap-2';
    const swatchButtons: HTMLButtonElement[] = [];
    const updateSwatchRing = (): void => {
      for (const btn of swatchButtons) {
        const isActive = btn.dataset.colorKey === selectedColor;
        btn.style.boxShadow = isActive ? `0 0 0 2px #0d1117, 0 0 0 4px ${getTagColorHex(btn.dataset.colorKey)}` : 'none';
      }
    };
    for (const c of TAG_COLORS) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.title = c.label;
      btn.dataset.colorKey = c.key;
      btn.className = 'rounded-full flex-shrink-0';
      btn.style.cssText = `width: 22px; height: 22px; background: ${c.hex};`;
      btn.addEventListener('click', () => {
        selectedColor = c.key;
        updateSwatchRing();
      });
      swatchButtons.push(btn);
      swatchGrid.appendChild(btn);
    }
    updateSwatchRing();
    form.appendChild(swatchGrid);

    const errorEl = document.createElement('div');
    errorEl.className = 'text-xs text-[#ef4444]';
    errorEl.style.display = 'none';
    form.appendChild(errorEl);

    const showError = (message: string): void => {
      errorEl.textContent = message;
      errorEl.style.display = 'block';
    };

    // "Implica" — the exact same add/remove mechanism as tagging an
    // ingredient (TagPicker: chips + a "+" that morphs into a chip-styled
    // autocomplete input). What it implies is only actually persisted on
    // Save (via setTagImplications, below), same as the name/color fields
    // above it — addFn here only resolves/creates the tag by name and
    // removeFn is a no-op; TagPicker's own onTagsChanged is what tracks
    // the pending set in the meantime. A brand new tag (no id yet) works
    // the same way — there's simply nothing to exclude from the "every
    // other tag" list.
    const impliesLabel = document.createElement('label');
    impliesLabel.className = 'block text-xs font-medium text-[#a0a0a0] font-mono uppercase tracking-wider';
    impliesLabel.textContent = 'Implica';
    form.appendChild(impliesLabel);

    const impliesContainer = document.createElement('div');
    form.appendChild(impliesContainer);

    let pendingImplies: Tag[] = existingTag
      ? this.impliesIdsOf(existingTag.id)
          .map(id => this.rows.find(r => r.tag.id === id)?.tag)
          .filter((t): t is Tag => !!t)
      : [];
    const allTagsExceptSelf = this.rows.map(r => r.tag).filter(t => t.id !== existingTag?.id);

    this.impliesPicker = new TagPicker({
      container: impliesContainer,
      idPrefix: `tag-implies-${existingTag?.id ?? 'new'}`,
      tags: pendingImplies,
      allTags: allTagsExceptSelf,
      addFn: async (name) => createOrGetTag(name),
      removeFn: async () => { /* pending only — applied on Save, like name/color */ },
      onTagsChanged: (tags) => { pendingImplies = tags; },
    });
    this.impliesPicker.render();

    const save = async (): Promise<void> => {
      const name = nameInput.value.trim();
      if (!name) {
        showError('El nom no pot estar buit');
        return;
      }
      try {
        const tag = existingTag
          ? await updateTag(existingTag.id, { name, color: selectedColor })
          : await createOrGetTag(name, selectedColor);

        await setTagImplications(tag.id, pendingImplies.map(t => t.id));
        tagGraphCache.invalidate();

        // Reload rather than patch the two local caches by hand: the
        // "Implica" picker above may have just created a brand new tag
        // (typing a name that didn't exist yet, same as the ingredient
        // tag picker's own create-mode addFn) — this is the simplest way
        // to make sure it actually shows up in the tags list too, not
        // just as this tag's implication.
        await this.loadRows();

        callbacks.onSaved(tag);
      } catch (error) {
        showError(error instanceof Error ? error.message : 'Error desant l\'etiqueta');
      }
    };
    const cancel = (): void => callbacks.onCancelled();

    nameInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        void save();
      } else if (e.key === 'Escape') {
        cancel();
      }
    });

    if (actionsContainer) {
      const saveBtn = this.buildIconButton(ICON_SAVE, 'Desa', true);
      saveBtn.addEventListener('click', (e) => { e.stopPropagation(); void save(); });
      const cancelBtn = this.buildIconButton(ICON_CANCEL, 'Cancel·la', false);
      cancelBtn.addEventListener('click', (e) => { e.stopPropagation(); cancel(); });
      actionsContainer.appendChild(saveBtn);
      actionsContainer.appendChild(cancelBtn);
    } else {
      const buttonsRow = document.createElement('div');
      buttonsRow.className = 'flex items-center gap-2';
      const saveBtn = document.createElement('button');
      saveBtn.type = 'button';
      saveBtn.textContent = existingTag ? 'Desa' : 'Crea';
      saveBtn.className = 'px-4 py-1.5 rounded-md bg-[var(--accent)] text-[#0d1117] text-sm font-mono hover:opacity-90';
      saveBtn.addEventListener('click', () => { void save(); });
      buttonsRow.appendChild(saveBtn);
      const cancelBtn = document.createElement('button');
      cancelBtn.type = 'button';
      cancelBtn.textContent = 'Cancel·la';
      cancelBtn.className = 'px-4 py-1.5 rounded-md border border-[var(--accent-alpha-40)] text-[var(--accent)] text-sm font-mono hover:bg-[var(--accent-alpha-13)]';
      cancelBtn.addEventListener('click', cancel);
      buttonsRow.appendChild(cancelBtn);
      form.appendChild(buttonsRow);
    }

    container.appendChild(form);
    nameInput.focus();
  }

  private mountViewContent(container: HTMLElement, row: TagRow): void {
    const { tag } = row;

    const wrapper = document.createElement('div');
    wrapper.className = 'space-y-3 pt-2';

    const header = document.createElement('div');
    header.className = 'flex items-center justify-center gap-2';
    header.appendChild(this.buildSwatchDot(tag.color));
    const nameEl = document.createElement('span');
    nameEl.className = 'text-sm text-[#e0e0e0] font-mono';
    nameEl.textContent = tag.name;
    header.appendChild(nameEl);
    wrapper.appendChild(header);

    // "Implica" — every tag transitively implied by this one (fruit
    // implies vegan implies vegetarian shows vegetarian on fruit's own
    // page too, not just vegan), rendered as tag chips rather than plain
    // text. tagGraphCache.getImpliedTags() already computes exactly this
    // closure — it's what every other tag-chip site uses to show a
    // ingredient's *implied* tags, and a single tag's own implications
    // are the same computation seeded with just itself.
    tagGraphCache.ensureLoaded().then(() => {
      if (this.selectedId !== tag.id) return;
      const implied = tagGraphCache.getImpliedTags([tag]);
      if (implied.length === 0) return;
      const impliesRow = document.createElement('div');
      impliesRow.className = 'flex flex-wrap items-center justify-center gap-1.5';
      const impliesLabel = document.createElement('span');
      impliesLabel.className = 'text-xs text-[#a0a0a0] font-mono';
      impliesLabel.textContent = 'Implica:';
      impliesRow.appendChild(impliesLabel);
      for (const t of implied) {
        const chip = document.createElement('span');
        chip.className = 'text-xs px-2 py-0.5 rounded-full border font-mono';
        chip.style.cssText = tagChipStyle(t.color);
        chip.textContent = t.name;
        impliesRow.appendChild(chip);
      }
      wrapper.insertBefore(impliesRow, ingredientsSection.section);
    }).catch((error) => {
      console.error('Failed to load tag implications:', error);
    });

    const ingredientsSection = this.buildIngredientsSection();
    wrapper.appendChild(ingredientsSection.section);

    container.appendChild(wrapper);

    const tagId = tag.id;
    getIngredientsForTag(tagId).then(ingredients => {
      if (this.selectedId !== tagId) return;
      this.renderPaginatedIngredientList(ingredientsSection, ingredients);
    }).catch(() => {
      if (this.selectedId !== tagId) return;
      ingredientsSection.list.textContent = 'Error carregant ingredients';
    });
  }

  private buildIngredientsSection(): { section: HTMLElement; label: HTMLElement; list: HTMLElement } {
    const section = document.createElement('div');
    section.className = 'space-y-1';

    const label = document.createElement('h3');
    label.className = 'text-xs font-mono uppercase tracking-wider text-[#a0a0a0]';
    label.textContent = 'Ingredients';
    section.appendChild(label);

    const list = document.createElement('div');
    list.className = 'space-y-1';
    list.textContent = 'Carregant...';
    section.appendChild(list);

    return { section, label, list };
  }

  private renderPaginatedIngredientList(
    { label, list }: { label: HTMLElement; list: HTMLElement },
    ingredients: Ingredient[],
  ): void {
    label.textContent = `Ingredients (${ingredients.length})`;

    if (ingredients.length === 0) {
      list.innerHTML = '';
      const empty = document.createElement('div');
      empty.className = 'text-xs text-[#666]';
      empty.textContent = 'Cap';
      list.appendChild(empty);
      return;
    }

    const totalPages = Math.ceil(ingredients.length / INGREDIENTS_PER_PAGE);
    let page = 0;

    const renderPage = (): void => {
      list.innerHTML = '';
      const start = page * INGREDIENTS_PER_PAGE;
      for (const ingredient of ingredients.slice(start, start + INGREDIENTS_PER_PAGE)) {
        list.appendChild(this.buildIngredientLink(ingredient));
      }

      if (totalPages > 1) {
        const pager = document.createElement('div');
        pager.className = 'flex items-center justify-center gap-3 pt-1';
        pager.appendChild(this.buildCyclerArrow('‹', () => {
          page = (page - 1 + totalPages) % totalPages;
          renderPage();
        }));
        const counter = document.createElement('span');
        counter.className = 'text-xs text-[#888] font-mono';
        counter.textContent = `${page + 1} / ${totalPages}`;
        pager.appendChild(counter);
        pager.appendChild(this.buildCyclerArrow('›', () => {
          page = (page + 1) % totalPages;
          renderPage();
        }));
        list.appendChild(pager);
      }
    };

    renderPage();
  }

  private buildIngredientLink(ingredient: Ingredient): HTMLElement {
    const a = document.createElement('a');
    a.href = `/ingredient/${ingredient.id}`;
    a.className = 'flex items-center gap-2 px-2 py-1 -mx-2 rounded-md hover:bg-[var(--accent-alpha-08)] text-sm text-[#e0e0e0]';
    const imageUrl = getDefaultImageUrl(ingredient.images);
    if (imageUrl) {
      const img = document.createElement('img');
      img.src = imageUrl;
      img.alt = '';
      img.className = 'rounded-full flex-shrink-0';
      img.style.cssText = 'width: 20px; height: 20px; object-fit: cover;';
      a.appendChild(img);
    }
    const span = document.createElement('span');
    span.className = 'truncate';
    span.textContent = ingredient.name;
    a.appendChild(span);
    return a;
  }

  /** Circular icon button — same look as ExplorerPage.ts's own toolbar/
   *  row-action buttons, so tags reads as the same visual language. */
  private buildIconButton(icon: string, title: string, active: boolean): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.title = title;
    btn.textContent = icon;
    btn.style.cssText = `
      width: 30px;
      height: 30px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 15px;
      font-weight: bold;
      font-family: inherit;
      border-radius: 50%;
      border: 1px solid ${active ? 'var(--accent)' : 'var(--accent-alpha-40)'};
      background: ${active ? 'var(--accent)' : 'transparent'};
      color: ${active ? '#0d1117' : 'var(--accent)'};
      cursor: pointer;
      transition: all 0.15s ease;
      flex-shrink: 0;
    `;
    return btn;
  }

  private buildCyclerArrow(glyph: string, onClick: () => void): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.textContent = glyph;
    btn.className = 'w-7 h-7 flex items-center justify-center rounded-full border border-[var(--accent-alpha-40)] text-[var(--accent)] hover:bg-[var(--accent-alpha-13)] font-mono';
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      onClick();
    });
    return btn;
  }

  private buildEditIconButton(): HTMLButtonElement {
    const btn = this.buildIconButton(ICON_EDIT, 'Edita', false);
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.isEditingCard = true;
      this.renderToolbar();
      this.refreshRows();
    });
    return btn;
  }

  /** Two-click arm/confirm delete — same mechanism/style as ExplorerPage.ts. */
  private buildDeleteIconButton(row: TagRow): HTMLButtonElement {
    const idleTitle = row.ingredientCount > 0 ? `Esborra (usada en ${row.ingredientCount})` : 'Esborra';
    const btn = this.buildIconButton(ICON_DELETE, idleTitle, false);

    let armed = false;
    let outsideHandler: ((e: MouseEvent) => void) | null = null;
    const disarm = (): void => {
      if (outsideHandler) {
        document.removeEventListener('click', outsideHandler);
        outsideHandler = null;
      }
      if (!armed) return;
      armed = false;
      btn.style.background = 'transparent';
      btn.style.color = 'var(--accent)';
      btn.style.borderColor = 'var(--accent-alpha-40)';
      btn.title = idleTitle;
    };

    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      if (!armed) {
        armed = true;
        btn.style.background = 'var(--accent)';
        btn.style.color = '#0d1117';
        btn.style.borderColor = 'var(--accent)';
        btn.title = 'Confirma l\'esborrat';
        outsideHandler = (ev: MouseEvent) => {
          if (ev.target !== btn) disarm();
        };
        document.addEventListener('click', outsideHandler);
        return;
      }
      disarm();
      void this.confirmDelete(row);
    });

    return btn;
  }

  private async confirmDelete(row: TagRow): Promise<void> {
    try {
      await deleteTag(row.tag.id);
      this.rows = this.rows.filter(r => r.tag.id !== row.tag.id);
      // The backend also drops every implication edge touching this tag
      // (see WriteService.delete_tag) — mirror that locally rather than
      // waiting on a reload.
      this.implications = this.implications.filter(
        i => i.fromTagId !== row.tag.id && i.toTagId !== row.tag.id
      );
      tagGraphCache.invalidate();
      this.selectedId = null;
      this.renderToolbar();
      this.refreshRows();
      showToast('Esborrada', 'success');
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Error esborrant', 'error');
    }
  }
}
